import { sectionNumber, } from './document';
import { generateContent, } from './document-generators';
import { documentTemplate, isRequiredTemplateKey } from './document-types';
import { locationPathLabel } from './location';
import { hasPlaceholder, isRichTextEmpty } from './rich-text';
import { buildSectionTree } from './section-tree';
export function renderDocument(input) {
    const { document, data } = input;
    const template = documentTemplate(document.documentTypeKey);
    const scope = { siteLocationId: document.siteLocationId, seriesIds: document.seriesIds };
    const generated = new Map();
    const contentFor = (kind) => {
        let content = generated.get(kind);
        if (!content) {
            content = generateContent(kind, scope, data);
            generated.set(kind, content);
        }
        return content;
    };
    const renderGenerated = (ownerId, child, positions) => ({
        id: `${ownerId}:${child.key}`,
        number: sectionNumber(positions),
        level: positions.length,
        title: child.title,
        source: 'generated',
        required: false,
        blocks: [],
        items: child.items,
        ...(child.seriesId ? { seriesId: child.seriesId } : {}),
        children: child.children.map((grandChild, index) => renderGenerated(ownerId, grandChild, [...positions, index + 1])),
    });
    const renderStored = (node, positions) => {
        const section = node.section;
        const generator = section.generator;
        const content = generator ? contentFor(generator.kind) : { items: [], sections: [] };
        const children = [
            ...content.sections.map((child, index) => renderGenerated(section.id, child, [...positions, index + 1])),
            ...node.children.map((child, index) => renderStored(child, [...positions, content.sections.length + index + 1])),
        ];
        return {
            id: section.id,
            number: sectionNumber(positions),
            level: positions.length,
            title: section.title,
            source: 'stored',
            sectionId: section.id,
            required: isRequiredTemplateKey(template, section.templateKey),
            ...(generator
                ? { generatorKind: generator.kind, blocksPlacement: generator.blocksPlacement }
                : {}),
            blocks: [...section.blocks].sort((a, b) => a.order - b.order),
            items: content.items,
            children,
        };
    };
    const tree = buildSectionTree(input.sections.filter((section) => !section.deletedAt));
    const byId = new Map(data.locations.map((node) => [node.id, node]));
    const site = byId.get(document.siteLocationId);
    const siteLabel = site
        ? locationPathLabel(site.path
            .map((id) => byId.get(id))
            .filter((node) => node !== undefined), ' › ')
        : undefined;
    return {
        documentId: document.id,
        title: document.title,
        ...(document.subtitle ? { subtitle: document.subtitle } : {}),
        documentTypeKey: document.documentTypeKey,
        documentTypeName: template?.name ?? document.documentTypeKey,
        siteLocationId: document.siteLocationId,
        ...(siteLabel ? { siteLabel } : {}),
        seriesIds: [...document.seriesIds],
        updatedAt: document.updatedAt,
        renderedAt: input.renderedAt,
        sections: tree.map((node, index) => renderStored(node, [index + 1])),
    };
}
// --- hints -------------------------------------------------------------------
export const DOCUMENT_HINT_KINDS = [
    'placeholder',
    'missingAltText',
    'emptySection',
    'seriesWithoutPoints',
    'pointsWithoutPlan',
    'locationMissing',
    'seriesMissing',
];
function hasVisibleContent(block) {
    if (block.type === 'richText' || block.type === 'note' || block.type === 'warning') {
        return !isRichTextEmpty(block.content);
    }
    return true;
}
/**
 * Hints in the editor (C1: information only; C2 blocks publishing on them).
 * Input is the render result, so the editor and a later publish check see
 * exactly the same document.
 */
export function collectDocumentHints(document) {
    const hints = [];
    const visit = (section, ownerId) => {
        const owner = section.sectionId ?? ownerId;
        const label = `${section.number} ${section.title}`;
        const add = (kind, message) => {
            hints.push({
                kind,
                renderedId: section.id,
                ...(owner ? { sectionId: owner } : {}),
                number: section.number,
                title: section.title,
                message,
            });
        };
        for (const block of section.blocks) {
            if ((block.type === 'richText' || block.type === 'note' || block.type === 'warning') &&
                hasPlaceholder(block.content)) {
                add('placeholder', `Kapitel ${label}: Platzhalter noch nicht ersetzt.`);
            }
            if (block.type === 'image' && !(block.asset.alternativeText ?? '').trim()) {
                add('missingAltText', `Kapitel ${label}: Bild ohne Alternativtext.`);
            }
        }
        if (section.source === 'stored' &&
            !section.blocks.some(hasVisibleContent) &&
            section.items.length === 0 &&
            section.children.length === 0) {
            add('emptySection', `Kapitel ${label} ist leer.`);
        }
        const placedPoints = new Set(section.items.flatMap((item) => item.kind === 'plans'
            ? item.plans.flatMap((figure) => figure.markers
                .filter((marker) => marker.kind === 'point')
                .map((marker) => marker.refId))
            : []));
        for (const item of section.items) {
            if (item.kind === 'notice' && item.code === 'locationMissing') {
                add('locationMissing', `Kapitel ${label}: Der gewählte Ort ist nicht mehr vorhanden.`);
            }
            if (item.kind === 'notice' && item.code === 'seriesMissing') {
                add('seriesMissing', `Kapitel ${label}: Eine gewählte Messreihe ist nicht mehr vorhanden.`);
            }
            if (item.kind === 'measurementTable') {
                if (item.table.rows.length === 0) {
                    add('seriesWithoutPoints', `Kapitel ${label}: Die Messreihe hat keine Messpunkte.`);
                }
                else {
                    const unplaced = item.table.rows.filter((row) => !placedPoints.has(row.pointId)).length;
                    if (unplaced > 0) {
                        add('pointsWithoutPlan', `Kapitel ${label}: ${unplaced === 1 ? '1 Messpunkt liegt' : `${unplaced} Messpunkte liegen`} auf keinem Plan.`);
                    }
                }
            }
        }
        for (const child of section.children)
            visit(child, owner);
    };
    for (const section of document.sections)
        visit(section, undefined);
    return hints;
}
//# sourceMappingURL=document-render.js.map